import { z } from 'zod'
import type { ComponentType, ReactNode } from 'react'
import type { AgentAttachment, AgentToolCall, ModelOption } from '@multiplayer-app/ai-agent-types'
import type { Translations } from './translations'

export type AgentTransportMode = 'proxy' | 'direct'

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

export interface AgentToolHandlerContext {
  chatId: string
  agentId?: string
  workspaceId?: string
  appendSystemMessage: (message: string) => void
}

export type AgentToolInvoke = (
  input: Record<string, unknown>,
  ctx: AgentToolHandlerContext
) => Promise<Record<string, unknown> | void>

export interface AgentToolDefinition {
  name: string
  label: string
  description?: string
  icon?: string
  schema?: Record<string, unknown>
  confirmation?: 'auto' | 'manual'
  category?: string
  handler?: AgentToolInvoke
}

export type AgentThemeColorMode = 'light' | 'dark'

export interface AgentThemeTokens {
  background: string
  surface: string
  text: string
  subtext: string
  border: string
  accent: string
  accentSoft: string
  warning: string
  danger: string
  fontFamily: string
  radius: string
  fontSize: string
  colorMode: AgentThemeColorMode
}

export const themeTokenSchema = z.object({
  background: z.string(),
  surface: z.string(),
  text: z.string(),
  subtext: z.string(),
  border: z.string(),
  accent: z.string(),
  accentSoft: z.string(),
  warning: z.string(),
  danger: z.string(),
  fontFamily: z.string(),
  radius: z.string(),
  fontSize: z.string(),
  colorMode: z.enum(['light', 'dark'])
})

export interface ContextKeyConfig {
  key: string
  label: string
  description?: string
  tools: string[]
  defaultModel?: string
  autoConfirmTools?: string[]
}

export type ConfirmationMode = 'auto' | 'human'

export interface AgentFeatureFlags {
  /**
   * Whether to render inline reasoning traces (AgentMessage.reasoning)
   * above assistant messages. When omitted, defaults to true.
   */
  reasoning?: boolean
  artifactsPanel?: boolean
  modelSwitching?: boolean
  toolConfiguration?: boolean
  /** Show the composer file-attach (paperclip) button. */
  fileAttachments?: boolean
  /** Show the composer attach-selection (web snippet) button. */
  selectionAttachments?: boolean
  multiAgentControl?: boolean
  sandboxControls?: boolean
  /**
   * Whether to show the chat history sidebar. Defaults to true.
   */
  historySidebar?: boolean
  /**
   * Strategy for generating chat titles:
   * - "local" (default): use cheap, local heuristics.
   * - "llm": allow an LLM-backed strategy when the transport supports it.
   */
  titleGeneration?: 'local' | 'llm'
  /**
   * Automatically attach "page context" (URL + metadata) to every outbound user message.
   * Defaults to true.
   */
  pageContextAttachments?: boolean
}

export type PageContextInput = {
  url?: string
  route?: string
  title?: string
}

export interface PageContextConfig {
  /**
   * Default options for the built-in `pageContext` attachment generation.
   * These are forwarded into `createPageContextAttachment(...)`.
   */
  includeUserAgent?: boolean
  includeReferrer?: boolean
  includeViewport?: boolean
  includeTitle?: boolean
  name?: string
  /**
   * Optional host hook to add additional structured metadata.
   * Returned object is merged into `metadata.data`.
   *
   * Keep this pure and fast; it runs on attach and on route changes.
   */
  getData?: (input: PageContextInput) => Record<string, unknown> | undefined
}

/** Input passed to chat-metadata providers when a new chat is created. */
export type ChatMetadataInput = {
  contextKey: string
}

export interface ChatMetadataConfig {
  /**
   * Optional host hook contributing metadata persisted on the chat
   * (`AgentChat.metadata`). Unlike `pageContext.getData` (per-message), this
   * runs ONCE when a new chat is created; the returned object is merged into
   * the new chat's `metadata`. Use for stable, chat-scoped facts (e.g. the
   * active document name/path, a tenant or record id).
   *
   * Keep this pure and fast. Returning `title` overrides the auto-generated
   * chat title.
   */
  getMetadata?: (input: ChatMetadataInput) => Record<string, unknown> | undefined
}

export type TransportBaseConfig = {
  mode: AgentTransportMode
  timeoutMs?: number
  headers?: Record<string, string>
  /**
   * Optional error handler invoked whenever the transport encounters an HTTP or streaming error.
   * Use this to surface errors to your own error reporting system or UI toast layer.
   */
  onError?: (error: Error) => void
}

export interface ProxyTransportConfig extends TransportBaseConfig {
  mode: 'proxy'
  baseUrl: string
  apiKey?: string
  socketPath?: string // Socket.IO path
  /**
   * Optional Socket.IO namespace to connect to, e.g. "/ai-agent".
   * When provided, the client will connect to `${socketUrl}${socketNamespace}`.
   */
  socketNamespace?: string
}

export interface DirectTransportConfig extends TransportBaseConfig {
  mode: 'direct'
  provider: 'openrouter' | 'openai' | 'custom'
  endpoint?: string
  apiKey?: string
  model?: string
  /**
   * Optional model to use specifically for title generation.
   * If not provided, falls back to the default `model`.
   * Useful for using a cheaper/faster model for titles while using
   * a more capable model for actual conversations.
   */
  titleGenerationModel?: string
}

export type AgentTransportConfig = ProxyTransportConfig | DirectTransportConfig

export interface ToolRendererProps {
  call: AgentToolCall
  contextKey: string
  chatId: string
  rejectButtonText?: string
  applyAllButtonText?: string
  showStatus?: boolean
}

export type ToolRendererComponent = ComponentType<ToolRendererProps>

export type ToolRendererRegistry = Record<string, ToolRendererComponent>

export type AgentPlanTaskStatus = 'pending' | 'in_progress' | 'done' | 'skipped'

export interface AgentPlanTask {
  id: string
  title: string
  status: AgentPlanTaskStatus
}

/** A point-in-time snapshot of the agent's task plan. */
export interface AgentPlan {
  title?: string
  tasks: AgentPlanTask[]
}

/**
 * Maps a tool call to a plan snapshot, or undefined when the call carries
 * none. The newest snapshot across the chat's tool calls drives the pinned
 * plan-progress panel above the composer; hosts register one extractor per
 * agent (e.g. reading `createBuildPlan` / `updateBuildTask` outputs).
 */
export type PlanExtractor = (call: AgentToolCall) => AgentPlan | undefined

export interface ComposerAttachmentActionsProps {
  /** Active chat id, or draft key when no chat is selected yet. */
  chatKey: string
  activeChatId?: string
  attachments: AgentAttachment[]
  addAttachments: (attachments: AgentAttachment[]) => void
  removeAttachment: (attachmentId: string) => void
}

export type ComposerAttachmentActionsComponent = ComponentType<ComposerAttachmentActionsProps>

export interface AgentFrontendConfig {
  appId: string
  workspaceId?: string
  debug?: boolean
  user?: {
    id: string
    displayName?: string
    email?: string
    avatarUrl?: string
  }
  contextKeys: ContextKeyConfig[]
  transport: AgentTransportConfig
  theme?: Partial<AgentThemeTokens>
  tools?: AgentToolDefinition[]
  models?: ModelOption[]
  features?: AgentFeatureFlags
  /**
   * Optional page context attachment configuration.
   * Useful for adding host-specific metadata (e.g. current tenant/project id).
   */
  pageContext?: PageContextConfig
  /**
   * Optional chat-level metadata configuration. Lets the host attach stable
   * metadata to a chat at creation time (persisted as `AgentChat.metadata`).
   */
  chatMetadata?: ChatMetadataConfig
  defaultContextKey?: string
  messagesPageSize?: number
  toolRenderers?: ToolRendererRegistry
  /**
   * Extracts plan snapshots from tool calls. When set, ChatWindow pins a
   * minimal plan-progress panel above the composer (collapsed: title +
   * progress + current task; expanded: the full task list). The panel hides
   * when the active chat has no plan-carrying tool calls.
   */
  planExtractor?: PlanExtractor
  /**
   * Optional React component rendered in the composer toolbar for host-defined attachment actions.
   * Use `addAttachments` to attach custom `AgentAttachment` payloads (e.g. context, links).
   */
  composerAttachmentActions?: ComposerAttachmentActionsComponent
  /**
   * Optional translations to override default English strings.
   * Partial translations are merged with defaults.
   */
  translations?: Partial<Translations>
  /**
   * Custom icons for context attachments keyed by `metadata.kind`.
   * When provided, overrides the default icon for that kind.
   * Example: `{ pageContext: <MyPageIcon />, formSnapshot: <MyFormIcon /> }`
   */
  contextAttachmentIcons?: Record<string, ReactNode>
}

export const contextKeySchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  tools: z.array(z.string()).default([]),
  defaultModel: z.string().optional(),
  autoConfirmTools: z.array(z.string()).optional()
})

export const frontendConfigSchema = z.object({
  appId: z.string(),
  workspaceId: z.string().optional(),
  debug: z.boolean().optional(),
  user: z
    .object({
      id: z.string(),
      displayName: z.string().optional(),
      email: z.string().optional(),
      avatarUrl: z.string().optional()
    })
    .optional(),
  contextKeys: z.array(contextKeySchema),
  transport: z.union([
    z.object({
      mode: z.literal('proxy'),
      baseUrl: z.string(),
      apiKey: z.string().optional(),
      socketPath: z.string().optional(),
      socketNamespace: z.string().optional(),
      timeoutMs: z.number().optional(),
      headers: z.record(z.string()).optional()
    }),
    z.object({
      mode: z.literal('direct'),
      provider: z.enum(['openrouter', 'openai', 'custom']),
      endpoint: z.string().optional(),
      apiKey: z.string().optional(),
      model: z.string().optional(),
      titleGenerationModel: z.string().optional(),
      timeoutMs: z.number().optional(),
      headers: z.record(z.string()).optional()
    })
  ]),
  theme: themeTokenSchema.partial().optional(),
  tools: z
    .array(
      z.object({
        name: z.string(),
        label: z.string(),
        description: z.string().optional(),
        icon: z.string().optional(),
        schema: z.record(z.any()).optional(),
        confirmation: z.enum(['auto', 'manual']).optional(),
        category: z.string().optional()
      })
    )
    .optional(),
  models: z
    .array(
      z.object({
        id: z.string(),
        label: z.string(),
        provider: z.string().optional(),
        reasoning: z.enum(['disabled', 'concise', 'deep']).optional(),
        contexts: z.array(z.string()).optional()
      })
    )
    .optional(),
  features: z
    .object({
      reasoning: z.boolean().optional(),
      artifactsPanel: z.boolean().optional(),
      modelSwitching: z.boolean().optional(),
      toolConfiguration: z.boolean().optional(),
      fileAttachments: z.boolean().optional(),
      selectionAttachments: z.boolean().optional(),
      multiAgentControl: z.boolean().optional(),
      sandboxControls: z.boolean().optional(),
      historySidebar: z.boolean().optional(),
      titleGeneration: z.enum(['local', 'llm']).optional(),
      pageContextAttachments: z.boolean().optional()
    })
    .optional(),
  pageContext: z
    .object({
      includeUserAgent: z.boolean().optional(),
      includeReferrer: z.boolean().optional(),
      includeViewport: z.boolean().optional(),
      includeTitle: z.boolean().optional(),
      name: z.string().optional()
    })
    .optional(),
  // getMetadata is a function, stripped by Zod and re-attached in AgentRuntime.
  chatMetadata: z.object({}).optional(),
  defaultContextKey: z.string().optional(),
  messagesPageSize: z.number().int().positive().max(200).optional(),
  translations: z
    .object({
      newChat: z.string().optional(),
      followUp: z.string().optional(),
      sendMessage: z.string().optional(),
      stopStreaming: z.string().optional(),
      typeMessageToSend: z.string().optional(),
      attachFiles: z.string().optional(),
      attachSelectionToContext: z.string().optional(),
      attachments: z.string().optional(),
      chats: z.string().optional(),
      multiUserChats: z.string().optional(),
      noMultiUserChats: z.string().optional(),
      noChatsYet: z.string().optional(),
      untitledChat: z.string().optional(),
      deleteChat: z.string().optional(),
      showChatHistory: z.string().optional(),
      hideChatHistory: z.string().optional(),
      fetchingChats: z.string().optional(),
      fetchingChatError: z.string().optional(),
      helloThere: z.string().optional(),
      howCanIHelpYou: z.string().optional()
    })
    .optional()
})

export type ValidatedAgentFrontendConfig = z.infer<typeof frontendConfigSchema> &
  Pick<
    AgentFrontendConfig,
    | 'toolRenderers'
    | 'planExtractor'
    | 'composerAttachmentActions'
    | 'tools'
    | 'pageContext'
    | 'chatMetadata'
    | 'contextAttachmentIcons'
  > & {
    translations: Translations
  }
